import { Controller } from "@hotwired/stimulus";

// The filter bar's client half (#58): facet changes apply themselves, GET
// submissions drop empty fields (the applied URL carries only real
// filters), and — while a filter is active — live card prepends into the
// grid are suppressed.
//
// The suppression is the deliberate answer to context-free broadcasts: a
// fresh upload's prepend knows nothing about the viewer's filter, so on a
// filtered grid it could push a non-matching card on top (or un-empty the
// "filtered to nothing" state). Only `prepend` streams aimed at the grid
// are dropped — `replace`/`remove` (live state flips, rejections) and the
// pagination frames' `append` streams flow untouched. A matching fresh
// upload stays invisible too until the next visit: self-healing, and
// honest either way. Revisit when post-alpha inline editing lands: a
// live attribute edit rides `replace`, which can leave a now-non-matching
// card in a filtered grid — today's only replaces are state flips, which
// never change filter membership.
export default class extends Controller {
  static targets = ["form"];
  static values = { filtered: Boolean, grid: String };

  connect() {
    // Always attached, filtered checked LIVE per event: a morph that
    // flips the value without replacing the element must not leave the
    // suppression stuck in either direction.
    this.suppressPrepends = (event) => {
      if (!this.filteredValue) return;
      const stream = event.detail.newStream;
      if (stream.action === "prepend" && stream.target === this.gridValue) {
        event.preventDefault();
      }
    };
    document.addEventListener(
      "turbo:before-stream-render",
      this.suppressPrepends,
    );

    // Arms the keyboard guard (see submit): on a CLOSED select every
    // arrow key changes the value and fires change — likewise a date
    // segment while typing. Only keys that can change a value arm the
    // guard; a pointer touch anywhere in the bar disarms it, so a
    // browsed-but-abandoned keystroke can never swallow a later click.
    // Accepted crack: a mouse pick inside a keyboard-OPENED native
    // picker stays local too (OS popups dispatch no pointerdown to the
    // page) — consistent with "keyboard opened the browse", and Apply
    // confirms as everywhere else.
    this.keyboardChange = false;
    this.armKeyboardGuard = (event) => {
      if (!event.target.matches("select, input[type=date]")) return;
      if (event.key === "Tab" || event.key === "Enter") return;
      this.keyboardChange = true;
    };
    this.disarmKeyboardGuard = () => {
      this.keyboardChange = false;
    };
    this.element.addEventListener("keydown", this.armKeyboardGuard, true);
    this.element.addEventListener(
      "pointerdown",
      this.disarmKeyboardGuard,
      true,
    );
  }

  disconnect() {
    document.removeEventListener(
      "turbo:before-stream-render",
      this.suppressPrepends,
    );
    this.element.removeEventListener("keydown", this.armKeyboardGuard, true);
    this.element.removeEventListener(
      "pointerdown",
      this.disarmKeyboardGuard,
      true,
    );
  }

  submit() {
    // A keyboard-driven change is browsing, not a decision: arrows on a
    // closed select fire change per step, and auto-submitting would
    // apply half-browsed values while the Turbo replace throws focus to
    // body — stealing the explicit Anwenden path that exists for
    // exactly these users. Pointer picks keep applying themselves;
    // keyboard confirms via Enter where the platform submits implicitly
    // from the focused control, or via the Anwenden button as the
    // universal path.
    if (this.keyboardChange) {
      this.keyboardChange = false;
      return;
    }
    this.formTarget.requestSubmit();
  }

  compact(event) {
    for (const [key, value] of [...event.formData.entries()]) {
      if (value === "") event.formData.delete(key);
    }
  }
}
